import { useState } from "react";

const initialRequest = {
    destination: "",
    departurePoint: "",
    destinationFlexibility: "fixed",
    departureDate: "",
    returnDate: "",
    adults: 1,
    children: 0,
    flexibleDates: false,
    dateFlexibility: "",
    budget: "",
    budgetType: "total",
    hotelCategory: "",
    mealType: "",
    dietaryPreference: "",
    transport: "",
    tripType: "",
    specialRequests: "",
};

const stepLabels = [
    "Destination",
    "Dates & travellers",
    "Preferences",
    "Review",
];

export default function TripWizard() {
    const [step, setStep] = useState(0);
    const [request, setRequest] = useState(initialRequest);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const update = (field, value) => {
        setRequest((current) => ({ ...current, [field]: value }));
    };

    const editStep = (targetStep) => {
        setError("");
        setStep(targetStep);
    };

    const validateCurrentStep = () => {
        if (step === 0) {
            return request.destination && request.departurePoint
                ? ""
                : "Please enter both destinations.";
        }

        if (step === 1) {
            if (!request.departureDate || !request.returnDate) {
                return "Please select departure and return dates.";
            }

            if (request.returnDate < request.departureDate) {
                return "The return date must be after the departure date.";
            }
        }

        if (step === 2) {
            return request.budget && request.hotelCategory && request.tripType
                ? ""
                : "Please complete your budget, hotel category, and trip type.";
        }

        return "";
    };

    const next = () => {
        const validationError = validateCurrentStep();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError("");
        setStep((current) => Math.min(current + 1, stepLabels.length - 1));
    };

    const submit = (event) => {
        event.preventDefault();

        for (let index = 0; index < stepLabels.length - 1; index += 1) {
            setStep(index);

            const validationError =
                index === 0
                    ? request.destination && request.departurePoint
                        ? ""
                        : "Please enter both destinations."
                    : index === 1
                        ? request.departureDate && request.returnDate
                            ? request.returnDate >= request.departureDate
                                ? ""
                                : "The return date must be after the departure date."
                            : "Please select departure and return dates."
                        : request.budget && request.hotelCategory && request.tripType
                            ? ""
                            : "Please complete your budget, hotel category, and trip type.";

            if (validationError) {
                setError(validationError);
                return;
            }
        }

        setStep(3);
        setError("");
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div role="status" className="trip-wizard-success box has-text-centered p-6">
                <span className="tag is-success is-light is-medium mb-4">Request submitted</span>
                <h2 className="title is-3">Trip request created</h2>
                <p className="subtitle is-6">
                    Your request for <strong>{request.destination}</strong> has been
                    submitted successfully.
                </p>
                <button
                    type="button"
                    className="button is-primary is-medium"
                    onClick={() => {
                        setRequest(initialRequest);
                        setStep(0);
                        setSubmitted(false);
                    }}
                >
                    Plan another trip
                </button>
            </div>
        );
    }

    return (
        <div className="trip-wizard-container container is-max-desktop px-4 py-5 has-background-white">
            <header className="has-text-centered mb-5">
                <h2 className="title is-2 mb-5">Get Free Travel Proposals</h2>
                <p className="subtitle is-6 has-text-grey mt-3">
                    Tell us what you have in mind. We'll help you find the right travel agent.
                </p>
            </header>
            <form className="trip-wizard" onSubmit={submit} noValidate>
                <ol className="trip-wizard__steps columns is-mobile is-gapless mb-5" aria-label="Trip request progress">
                    {stepLabels.map((label, index) => (
                        <li
                            key={label}
                            aria-current={index === step ? "step" : undefined}
                            className={`column has-text-centered ${index === step ? "active" : index < step ? "complete" : ""}`}
                        >
                            <span className={`tag is-rounded mb-2 ${index <= step ? "is-ta-orange" : "is-light"}`}>
                                {index < step ? "✓" : index + 1}
                            </span>
                            <p className={`is-size-7 ${index === step ? "txt-ta-orange has-text-weight-semibold" : "has-text-grey"}`}>
                                {label}
                            </p>
                        </li>
                    ))}
                </ol>

                {error && (
                    <p role="alert" className="trip-wizard__error notification is-danger is-light">
                        {error}
                    </p>
                )}

                {step === 0 && (
                    <section className="box p-5">
                        <h2 className="title is-4">Where are you travelling?</h2>
                        <p className="subtitle is-6 has-text-grey">Choose your destination and where you'll start.</p>

                        <label className="field">
                            <span className="label">Destination</span>
                            <input
                                className="input"
                                value={request.destination}
                                onChange={(event) => update("destination", event.target.value)}
                                placeholder="Paris, France"
                            />
                        </label>

                        <label className="field">
                            <span className="label">Starting point</span>
                            <input
                                className="input"
                                value={request.departurePoint}
                                onChange={(event) => update("departurePoint", event.target.value)}
                                placeholder="Delhi, India"
                            />
                        </label>

                        <fieldset className="field">
                            <legend className="label">Destination flexibility</legend>
                            <label className="radio mr-4">
                                <input
                                    type="radio"
                                    className="mr-1"
                                    checked={request.destinationFlexibility === "fixed"}
                                    onChange={() => update("destinationFlexibility", "fixed")}
                                />
                                Destinations are fixed
                            </label>
                            <label className="radio">
                                <input
                                    type="radio"
                                    className="mr-1"
                                    checked={request.destinationFlexibility === "open"}
                                    onChange={() => update("destinationFlexibility", "open")}
                                />
                                Open to suggestions
                            </label>
                        </fieldset>
                    </section>
                )}

                {step === 1 && (
                    <section className="box p-5">
                        <h2 className="title is-4">Dates and travellers</h2>
                        <p className="subtitle is-6 has-text-grey">When are you going, and who is coming along?</p>

                        <label className="field">
                            <span className="label">Departure date</span>
                            <input
                                className="input"
                                type="date"
                                value={request.departureDate}
                                onChange={(event) => update("departureDate", event.target.value)}
                            />
                        </label>

                        <label className="field">
                            <span className="label">Return date</span>
                            <input
                                className="input"
                                type="date"
                                value={request.returnDate}
                                min={request.departureDate}
                                onChange={(event) => update("returnDate", event.target.value)}
                            />
                        </label>

                        <div className="columns">
                            <label className="field column">
                                <span className="label">Adults</span>
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    value={request.adults}
                                    onChange={(event) =>
                                        update("adults", Math.max(1, Number(event.target.value)))
                                    }
                                />
                            </label>

                            <label className="field column">
                                <span className="label">Children</span>
                                <input
                                    className="input"
                                    type="number"
                                    min="0"
                                    value={request.children}
                                    onChange={(event) =>
                                        update("children", Math.max(0, Number(event.target.value)))
                                    }
                                />
                            </label>
                        </div>

                        <label className="checkbox mb-4">
                            <input
                                type="checkbox"
                                className="mr-2"
                                checked={request.flexibleDates}
                                onChange={(event) => update("flexibleDates", event.target.checked)}
                            />
                            My dates are flexible
                        </label>

                        {request.flexibleDates && (
                            <label className="field">
                                <span className="label">Date flexibility</span>
                                <input
                                    className="input"
                                    value={request.dateFlexibility}
                                    onChange={(event) =>
                                        update("dateFlexibility", event.target.value)
                                    }
                                    placeholder="e.g. within 2 weeks"
                                />
                            </label>
                        )}
                    </section>
                )}

                {step === 2 && (
                    <section className="box p-5">
                        <h2 className="title is-4">Preferences and budget</h2>
                        <p className="subtitle is-6 has-text-grey">A few details will help us find the right trip for you.</p>

                        <label className="field">
                            <span className="label">Budget</span>
                            <input
                                className="input"
                                type="number"
                                min="0"
                                value={request.budget}
                                onChange={(event) => update("budget", event.target.value)}
                            />
                        </label>

                        <fieldset className="field">
                            <legend className="label">Budget basis</legend>
                            <label className="radio mr-4">
                                <input
                                    type="radio"
                                    className="mr-1"
                                    checked={request.budgetType === "total"}
                                    onChange={() => update("budgetType", "total")}
                                />
                                Total budget
                            </label>
                            <label className="radio">
                                <input
                                    type="radio"
                                    className="mr-1"
                                    checked={request.budgetType === "person"}
                                    onChange={() => update("budgetType", "person")}
                                />
                                Per person
                            </label>
                        </fieldset>

                        <label className="field">
                            <span className="label">Hotel category</span>
                            <span className="select is-fullwidth">
                                <select
                                    value={request.hotelCategory}
                                    onChange={(event) => update("hotelCategory", event.target.value)}
                                >
                                    <option value="">Select category</option>
                                    <option value="1-star">1 star</option>
                                    <option value="2-star">2 star</option>
                                    <option value="3-star">3 star</option>
                                    <option value="4-star">4 star</option>
                                    <option value="5-star">5 star</option>
                                </select>
                            </span>
                        </label>

                        <label className="field">
                            <span className="label">Meal type</span>
                            <span className="select is-fullwidth">
                                <select
                                    value={request.mealType}
                                    onChange={(event) => update("mealType", event.target.value)}
                                >
                                    <option value="">No preference</option>
                                    <option value="Room only">Room only</option>
                                    <option value="Bed and breakfast">Bed and breakfast</option>
                                    <option value="Half board">Half board</option>
                                    <option value="Full board">Full board</option>
                                    <option value="All-inclusive">All-inclusive</option>
                                </select>
                            </span>
                        </label>

                        <label className="field">
                            <span className="label">Dietary preference</span>
                            <span className="select is-fullwidth">
                                <select
                                    value={request.dietaryPreference}
                                    onChange={(event) =>
                                        update("dietaryPreference", event.target.value)
                                    }
                                >
                                    <option value="">No preference</option>
                                    <option value="Vegetarian">Vegetarian</option>
                                    <option value="Non-vegetarian">Non-vegetarian</option>
                                    <option value="Vegan">Vegan</option>
                                    <option value="Pescatarian">Pescatarian</option>
                                </select>
                            </span>
                        </label>

                        <label className="field">
                            <span className="label">Preferred transport</span>
                            <span className="select is-fullwidth">
                                <select
                                    value={request.transport}
                                    onChange={(event) => update("transport", event.target.value)}
                                >
                                    <option value="">No preference</option>
                                    <option value="flight">Flight</option>
                                    <option value="train">Train</option>
                                    <option value="bus">Bus</option>
                                    <option value="self-drive">Self-drive</option>
                                </select>
                            </span>
                        </label>

                        <label className="field">
                            <span className="label">Trip type</span>
                            <span className="select is-fullwidth">
                                <select
                                    value={request.tripType}
                                    onChange={(event) => update("tripType", event.target.value)}
                                >
                                    <option value="">Select trip type</option>
                                    <option value="family">Family</option>
                                    <option value="honeymoon">Honeymoon</option>
                                    <option value="adventure">Adventure</option>
                                    <option value="pilgrimage">Pilgrimage</option>
                                    <option value="business">Business</option>
                                    <option value="other">Other</option>
                                </select>
                            </span>
                        </label>

                        <label className="field">
                            <span className="label">Special requests <span className="has-text-grey-light has-text-weight-normal">(optional)</span></span>
                            <textarea
                                className="textarea"
                                value={request.specialRequests}
                                onChange={(event) =>
                                    update("specialRequests", event.target.value)
                                }
                                rows={4}
                            />
                        </label>
                    </section>
                )}

                {step === 3 && (
                    <section className="box p-5">
                        <h2 className="title is-4">Review your trip request</h2>
                        <p className="subtitle is-6 has-text-grey">Make sure everything looks right before submitting.</p>

                        <ReviewSection title="Destination" onEdit={() => editStep(0)}>
                            <p><strong>Destination:</strong> {request.destination}</p>
                            <p><strong>Starting point:</strong> {request.departurePoint}</p>
                            <p>
                                <strong>Flexibility:</strong>{" "}
                                {request.destinationFlexibility === "fixed"
                                    ? "Fixed"
                                    : "Open to suggestions"}
                            </p>
                        </ReviewSection>

                        <ReviewSection title="Dates and travellers" onEdit={() => editStep(1)}>
                            <p>
                                <strong>Dates:</strong> {request.departureDate} – {request.returnDate}
                            </p>
                            <p><strong>Adults:</strong> {request.adults}</p>
                            <p><strong>Children:</strong> {request.children}</p>
                            <p>
                                <strong>Flexible dates:</strong>{" "}
                                {request.flexibleDates
                                    ? request.dateFlexibility || "Yes"
                                    : "No"}
                            </p>
                        </ReviewSection>

                        <ReviewSection title="Preferences" onEdit={() => editStep(2)}>
                            <p>
                                <strong>Budget:</strong> {request.budget} ({request.budgetType})
                            </p>
                            <p><strong>Hotel:</strong> {request.hotelCategory}</p>
                            <p>
                                <strong>Meal type:</strong>{" "}
                                {request.mealType || "No preference"}
                            </p>
                            <p>
                                <strong>Dietary preference:</strong>{" "}
                                {request.dietaryPreference || "No preference"}
                            </p>
                            <p>
                                <strong>Transport:</strong>{" "}
                                {request.transport || "No preference"}
                            </p>
                            <p><strong>Trip type:</strong> {request.tripType}</p>
                            <p>
                                <strong>Special requests:</strong>{" "}
                                {request.specialRequests || "None"}
                            </p>
                        </ReviewSection>

                        <button className="button is-primary is-medium is-fullwidth mt-5" type="submit">
                            Submit trip request
                        </button>
                    </section>
                )}

                <div className="trip-wizard__actions is-flex is-justify-content-space-between mt-5">
                    {step > 0 && (
                        <button className="button is-light" type="button" onClick={() => setStep((current) => current - 1)}>
                            Back
                        </button>
                    )}
                    {step < stepLabels.length - 1 && (
                        <button className="button is-link ml-auto" type="button" onClick={next}>
                            Continue
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}

function ReviewSection({ title, children, onEdit }) {
    return (
        <article className="box has-background-light mb-4">
            <div className="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <h3 className="title is-6 mb-0">{title}</h3>
                <button
                    className="button is-small is-light"
                    type="button"
                    onClick={onEdit}
                    aria-label={`Edit ${title}`}
                    title={`Edit ${title}`}
                >
                    <span className="icon" aria-hidden="true">✎</span>
                    <span>Edit</span>
                </button>
            </div>
            <div className="content is-small mb-0">{children}</div>
        </article>
    );
}